import type { LngLat } from '../../../maps/mapboxDrivingRoute';
import { distanceMeters } from '../../../maps/mapboxDrivingRoute';
import { resolveFlightType } from '../../smart-time/flightTimeService';
import type { SmartTimeFlightDetails } from '../../smart-time/smartTimeTypes';
import yyzGroundLevelGrid from './yyzTerminal1GroundLevelGrid.json';

/**
 * YYZ Terminal 1 arrival-entry anchor points, keyed by flight scope.
 *
 * After landing, a passenger exits the sterile corridor into the public
 * arrivals hall (toward baggage claim) through one of two doorways depending
 * on the direction the flight came from:
 *
 *   - Domestic flight      -> the near curbside exit, right by the pickup point
 *   - International flight -> the Canada Border Services Agency (customs) exit
 *
 * Both funnel to the SAME destination — the trip's real curbside pickup
 * point (Door A) — never a different door per scope. That part is confirmed
 * directly by the product owner: T1 always uses the one pickup door for both
 * domestic and international arrivals; only the START of the walk differs.
 *
 * # How these anchors were sourced and checked
 *
 * Neither the Mapbox indoor tileset nor the bundled OSM graph labels a point
 * "Canada Arrivals"/"International Arrivals" — those are terminal signage
 * wording, not tileset feature names. Instead of guessing, each anchor below
 * is a REAL feature already present in the data, picked because it matches
 * the passenger's own on-site walking-distance measurements to within a few
 * metres:
 *
 * - **International**: `Canada Border Services Agency` — a government-
 *   labelled `indoor_label` feature on T1's Baggage Claim Level (class
 *   `services`, at [-79.61056262254715, 43.68135343085953]), i.e. the real
 *   customs/immigration checkpoint every international arrival clears.
 *   Straight-line distance to the curb pickup point: **135.2 m**. The
 *   passenger's own measured international walk (left 21.63 m, left 6.31 m,
 *   straight 108 m) totals **135.94 m** — a 0.5% difference, not a
 *   coincidence for an unrelated point.
 * - **Domestic**: the ground-level `externalDoors[0]` entry already in the
 *   bundled Ground Transportation Level grid — the curbside door nearest the
 *   pickup point. Straight-line distance: **18.7 m**, against the
 *   passenger's measured **13 m** domestic walk (a plain door-threshold vs.
 *   pickup-point straight-line distance is expected to run a bit longer than
 *   a walked distance measured from just inside the door).
 *
 * These are real, sourced points with numbers that line up with ground
 * truth the product owner collected in person — not placeholders — but they
 * are still each a SINGLE point standing in for "the doorway a passenger
 * actually walks through," which in reality has some width. If the walk
 * ever visibly starts from the wrong spot on-site, re-derive against the
 * same tileset (`indoor_label`, `services`/`ride_sharing_services` classes)
 * rather than reverting to a guess.
 *
 * Because all arriving passengers funnel into the same baggage-claim hall
 * regardless of scope, the distinction is only which start point to route
 * FROM — the destination (the trip's real pickup point) is unchanged.
 *
 * # Fallback
 *
 * `pickYyzArrivalStart` returns null when there are no flight details
 * available (and therefore no way to know which arrival door applies);
 * callers fall back to the long-standing default start (Carousel 1 on the
 * baggage claim level) rather than failing closed on a configurable nicety.
 * Once flight details ARE present, `resolveFlightType` always yields a
 * concrete scope, so a genuine domestic vs international decision is always
 * made.
 */

/**
 * Domestic arrival exit. The Ground Transportation Level's own curbside
 * external door nearest the pickup point — read from the bundled grid
 * directly so it can't drift from the source data.
 */
const CANADA_ARRIVALS_DOOR: LngLat = yyzGroundLevelGrid.externalDoors[0] as LngLat;

/**
 * International arrival exit — the real, government-labelled "Canada
 * Border Services Agency" (customs) point on Baggage Claim Level. See the
 * module doc above for the distance verification against the passenger's
 * own on-site measurements.
 */
const INTERNATIONAL_ARRIVALS_DOOR: LngLat = [-79.61056262254715, 43.68135343085953];

export const YYZ_ARRIVAL_ENTRIES: Record<'Domestic' | 'International', LngLat> = {
  Domestic: CANADA_ARRIVALS_DOOR,
  International: INTERNATIONAL_ARRIVALS_DOOR,
};

/** The single curbside pickup door — Terminal 1 uses the same one for both
 * domestic and international arrivals, never a different door per scope.
 * Single source of truth for every screen that needs to recognize "this
 * trip's pickup is YYZ Terminal 1's curb" (`WalkToPickupScreen`,
 * `WalkToPickupStepsScreen`) — read from the bundled grid so it can't drift
 * out of sync between them. */
export const YYZ_T1_PICKUP: LngLat = yyzGroundLevelGrid.pickupPoint as LngLat;

/** How close a trip's real pickup coordinate needs to be to the known YYZ
 * Terminal 1 curb to treat this walk as a YYZ T1 arrival (real indoor
 * routing, the recorded written-directions steps) instead of the generic
 * fallback used everywhere we don't have this terminal's data. Loose enough
 * to absorb minor trip-data/geocoding drift, tight enough that it can't
 * accidentally match some other, unrelated nearby location. */
const YYZ_T1_MATCH_RADIUS_METERS = 200;

export function isYyzTerminal1Pickup(point: LngLat | null): boolean {
  return point != null && distanceMeters(point, YYZ_T1_PICKUP) <= YYZ_T1_MATCH_RADIUS_METERS;
}

/** Distance (metres) from a flight scope's arrival anchor to the trip's
 * pickup point — exposed so the static/manual walk guide can show a real,
 * data-derived distance alongside the passenger's own verified step text. */
export function yyzArrivalDistanceToPickup(scope: 'Domestic' | 'International', pickup: LngLat): number {
  return distanceMeters(YYZ_ARRIVAL_ENTRIES[scope], pickup);
}

/**
 * The flight-appropriate arrival door to start the walk from, or null when the
 * flight's scope can't be known (no flight details available). Once a flight
 * is present, `resolveFlightType` yields a concrete Domestic/International so
 * a real door is always chosen. Null -> the caller uses the Carousel 1
 * default instead; see the module doc.
 */
export function pickYyzArrivalStart(
  flight?: SmartTimeFlightDetails | null | undefined,
): LngLat | null {
  if (!flight) return null;
  return YYZ_ARRIVAL_ENTRIES[resolveFlightType(flight)];
}

export type YyzTerminal = 'T1' | 'T3';

/**
 * YYZ Terminal 3's curbside pickup door ("Door A") — verified directly
 * against the bundled OSM indoor graph's entrances list
 * (`yyzTerminal1OsmGraph.json`'s `entrances`, which also carries Terminal
 * 3's lettered doors/gates despite the file's T1-specific name; see
 * CLAUDE.md's `indoor_structure_metadata` note on why the two terminals'
 * doors coexist in the same bundled graph). Single source of truth for
 * "this trip's pickup is YYZ Terminal 3's curb", mirroring `YYZ_T1_PICKUP`.
 */
export const YYZ_T3_PICKUP: LngLat = [-79.6213859, 43.6869934];

/** Same reasoning as `YYZ_T1_MATCH_RADIUS_METERS` — loose enough to absorb
 * minor trip-data/geocoding drift, tight enough not to false-match some
 * other nearby location. */
const YYZ_T3_MATCH_RADIUS_METERS = 200;

export function isYyzTerminal3Pickup(point: LngLat | null): boolean {
  return point != null && distanceMeters(point, YYZ_T3_PICKUP) <= YYZ_T3_MATCH_RADIUS_METERS;
}

/**
 * Which known YYZ terminal (if any) a trip's real pickup coordinate belongs
 * to — lets the written-directions screen pick its recorded steps/photos
 * from location alone, the same way `isYyzTerminal1Pickup` alone used to
 * gate the (T1-only) walk guide. The two curbs are roughly 1.1km apart, far
 * enough outside each other's match radius that checking in a fixed order
 * is safe.
 */
export function resolveYyzTerminal(point: LngLat | null): YyzTerminal | null {
  if (isYyzTerminal1Pickup(point)) return 'T1';
  if (isYyzTerminal3Pickup(point)) return 'T3';
  return null;
}

/**
 * Precise, on-site-measured pickup BOOTHS per terminal — the walk's actual
 * DESTINATION, one level more specific than `YYZ_T1_PICKUP`/`YYZ_T3_PICKUP`
 * above (which are each a single approximate curb point standing in for
 * "this terminal's pickup area," not a particular booth).
 *
 * Mirrors `YYZ_ARRIVAL_ENTRIES`'s Domestic/International split on the START
 * side, but for the END of the walk. `Prearranged` is a third, SHARED booth
 * — Hopn's own in-app arrival guide (`AirportGuideContent.tsx`) already
 * tells every passenger to "locate gate 'A' at the pre-arranged limo booth"
 * regardless of flight type, and the Terms & Conditions describe every
 * booking on this platform as pre-arranged (never a curb-hail), so it is
 * the correct fallback whenever a flight's domestic/international scope
 * isn't known — same null-safe shape as `pickYyzArrivalStart` falling back
 * to Carousel 1 when there's no flight to resolve.
 */
export type YyzPickupScope = 'Domestic' | 'International' | 'Prearranged';

const YYZ_T1_PICKUP_BOOTHS: Record<YyzPickupScope, LngLat> = {
  International: [-79.61193, 43.68185],
  Domestic: [-79.61282, 43.68205],
  Prearranged: [-79.61274, 43.68205],
};

const YYZ_T3_PICKUP_BOOTHS: Record<YyzPickupScope, LngLat> = {
  International: [-79.6185, 43.68571],
  Domestic: [-79.62115, 43.68654],
  Prearranged: [-79.62133, 43.68713],
};

export const YYZ_PICKUP_BOOTHS: Record<YyzTerminal, Record<YyzPickupScope, LngLat>> = {
  T1: YYZ_T1_PICKUP_BOOTHS,
  T3: YYZ_T3_PICKUP_BOOTHS,
};

/**
 * The flight-appropriate pickup booth for a known YYZ terminal — the walk's
 * destination, replacing the trip's own (coarser, geocoded) pickup
 * coordinate once it's been recognized as this terminal's curb. Falls back
 * to the shared `Prearranged` booth when the flight's scope isn't known yet
 * (no flight details available) — always a concrete coordinate, never null,
 * since (unlike the walk's start) there is no "generic default" pickup
 * point to fall back to instead.
 */
export function pickYyzArrivalPickup(
  terminal: YyzTerminal,
  flight?: SmartTimeFlightDetails | null,
): LngLat {
  const booths = YYZ_PICKUP_BOOTHS[terminal];
  if (!flight) return booths.Prearranged;
  return booths[resolveFlightType(flight)];
}
